import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';

import { cn } from '../utils/cn';

import { LogoInsolitum } from './LogoInsolitum';
import { PodpisInsolitum } from './PodpisInsolitum';
import { SygnetRozsypany } from './SygnetRozsypany';
import { SKALA_ZNAKU } from './geometria';
import type { WariantRozpadu } from './rozpad';
import { bezSierotekWTresci } from './typografia';

// Typografia ekranów stanu NIE jest stąd przekazywana dalej: strona Next,
// która nie bierze beczki `@insolitum/ui`, importuje ją z podścieżki
// `@insolitum/ui/marka/typografia`. Import przez ten plik wciągał do strony
// cały ekran błędu (logowanie B2B 2,7 → 4,9 kB) — reeksport, którego nikt już
// nie używał, zapraszał do powtórzenia tej usterki. Bramka:
// `tests/unit/marka/typografiaEkranuStanu.test.tsx`.

/**
 * Ekran błędu — jeden wygląd każdej strony awarii w całym systemie. Aplikacje
 * podają tylko akcje (bo tylko one wiedzą, dokąd wrócić i jak ponowić);
 * ilustracja, układ i domyślne zdania są tu.
 *
 * Ilustracją jest sam znak w stanie awarii (`SygnetRozsypany`): rdzeń to
 * żywa sieć i Nexus, odłamki to organy — więc ekran pokazuje, co stało się
 * z organami, a nie ogólny obrazek „ups".
 *
 * Głos (brief 02 §5.2): spokojnie, z rozwiązaniem, bez obwiniania. Każdy
 * ekran mówi CO się stało i CO zrobić.
 *
 * Brak dostępu (403) NIE jest rozsypany: system działa, to człowiek nie ma
 * uprawnień. Pokazuje znak w spoczynku — w kolorze znaku, jak cała rodzina
 * (szary znak czytałby się jak „wyłączony”; „to nie awaria” mówi kształt).
 *
 * Postać ekranu (`ilustracja`): bez propa — znak w stanie wariantu; element
 * (np. kafel organu na odmowie bramki) — w miejscu znaku; `null` — bez
 * postaci, tekst od nadtytułu. Pudełko znaku to NOMINALNY sygnet (96 px,
 * 80 px na telefonie, 7,5rem w gęstości kiosk); pole ochronne i odłamki
 * rozpadu rysują się poza pudełkiem, więc odstęp w CSS jest odstępem
 * widocznym. Rdzeń stoi zawsze w tym samym miejscu: `środek obszaru − 10rem`
 * (`--ids-ekran-kotwica`), niezależnie od długości treści i wiersza stanu.
 *
 * Podpis na dole układu `pelny`: przy domyślnej postaci sam napis
 * (README pakietu: drugi sygnet w widoku to o jeden za dużo), przy własnej
 * postaci albo bez niej — logo poziome (`PodpisInsolitum`). Zawsze 24 px od
 * dołu, w przepływie.
 *
 * Akcje: pierwsza `btn btn--primary btn--lg`, drugorzędna
 * `btn btn--secondary btn--lg` — w wierszu wyśrodkowanym każdy przycisk musi
 * mieć widoczne pudełko, inaczej środek optyczny ucieka o pół paddingu
 * (bramka `tests/unit/marka/akcjeEkranuBledu.test.ts` nie wpuszcza `btn--ghost`).
 *
 * Typografia: jednoliterowy spójnik albo przyimek nie zostaje na końcu
 * wiersza — w nadtytule, tytule i treści spacja po nim staje się twardą
 * (`bezSierotekWTresci` z `typografia.ts`, także w treści podanej jako
 * `<p>…</p>`; komponentów w treści nie dotyka). Jedno miejsce dla całej
 * rodziny ekranów stanu — aplikacje nie wstawiają `&nbsp;` po swojemu.
 *
 * Komponent jest serwerowy (bez hooków). Akcje mogą być elementami
 * klienckimi przekazanymi z zewnątrz; adaptery Next (`marka/next/*`) dodają
 * ponawianie i samonaprawę znaku.
 */
export type WariantEkranuBledu = WariantRozpadu | 'brak-dostepu';

interface Tresc {
  readonly nadtytul: string;
  readonly tytul: string;
  readonly tresc: string;
}

export const TRESCI_EKRANU_BLEDU: Readonly<Record<WariantEkranuBledu, Tresc>> = {
  'nie-znaleziono': {
    nadtytul: 'Błąd 404',
    tytul: 'Nie znaleźliśmy tej strony',
    tresc:
      'Adres mógł się zmienić albo strona została przeniesiona. Reszta systemu działa normalnie.',
  },
  blad: {
    nadtytul: 'Nieoczekiwany błąd',
    tytul: 'Coś poszło nie tak po naszej stronie',
    tresc:
      'Zapisane dane są bezpieczne. Spróbuj ponownie — jeśli błąd się powtórzy, prześlij nam szczegóły techniczne.',
  },
  polaczenie: {
    nadtytul: 'Brak połączenia',
    tytul: 'Jesteś offline',
    tresc: 'Sprawdź Wi-Fi lub kabel sieciowy. Połączymy się automatycznie, gdy sieć wróci.',
  },
  serwis: {
    nadtytul: 'Prace serwisowe',
    tytul: 'Trwają zaplanowane prace serwisowe',
    tresc: 'Wrócimy za kilka minut. Zapisane dane są bezpieczne — nic nie musisz robić.',
  },
  'brak-dostepu': {
    nadtytul: 'Błąd 403',
    tytul: 'Nie masz dostępu do tej strony',
    tresc:
      'Dostęp nadaje administrator Twojej organizacji. Jeśli to pomyłka, poproś go o uprawnienia.',
  },
  'organ-niedostepny': {
    nadtytul: 'Organ niedostępny',
    tytul: 'Ten organ chwilowo nie odpowiada',
    tresc:
      'Pozostałe organy działają normalnie, a dane organu są bezpieczne. Spróbuj ponownie za chwilę.',
  },
};

export interface SzczegolyBledu {
  /** Kod błędu (np. „500", „CHUNK_LOAD"). */
  kod?: string;
  /** Identyfikator zdarzenia (np. `digest` z Next) — do przepisania ze zdjęcia ekranu. */
  identyfikator?: string;
  sciezka?: string;
  wersja?: string;
}

export interface EkranBleduProps extends Omit<HTMLAttributes<HTMLElement>, 'title'> {
  wariant: WariantEkranuBledu;
  /** `pelny` — cała strona; `w-tresci` — wnętrze layoutu (pasek boczny zostaje). */
  uklad?: 'pelny' | 'w-tresci';
  /** Nazwa organu dla wariantu `organ-niedostepny` („Agenda chwilowo nie odpowiada"). */
  organ?: string;
  nadtytul?: ReactNode;
  tytul?: ReactNode;
  tresc?: ReactNode;
  /**
   * Przyciski / linki: `btn btn--primary btn--lg`, drugorzędny
   * `btn btn--secondary btn--lg` (nie `btn--ghost` — bez widocznego pudełka
   * wiersz traci oś).
   */
  akcje?: ReactNode;
  /**
   * Wiersz stanu pod akcjami (np. „Kolejna próba za 10 s"); ogłaszany
   * uprzejmie. Wiersz istnieje tylko, gdy prop jest podany (także pusty
   * napis) — adapter z ponawianiem podaje `''` od pierwszego renderu, żeby
   * region `aria-live` stał, zanim przyjdzie komunikat. Pusty wiersz nie
   * zajmuje miejsca (`:empty` w `marka.css`), z treścią rezerwuje linię
   * (`min-height: 1.5em`), więc zmiana jednego komunikatu na drugi nie skacze.
   */
  stan?: ReactNode;
  szczegoly?: SzczegolyBledu;
  /**
   * Postać ekranu. Bez propa — znak w stanie wariantu; element — własna
   * postać (kafel organu); `null` — bez postaci.
   */
  ilustracja?: ReactNode;
}

export const EkranBledu = forwardRef<HTMLElement, EkranBleduProps>(function EkranBledu(
  {
    wariant,
    uklad = 'w-tresci',
    organ,
    nadtytul,
    tytul,
    tresc,
    akcje,
    stan,
    szczegoly,
    ilustracja,
    className,
    ...props
  },
  ref
) {
  const t = TRESCI_EKRANU_BLEDU[wariant];
  const Korzen = uklad === 'pelny' ? 'main' : 'section';
  const postac: 'znak' | 'wlasna' | 'brak' =
    ilustracja === undefined
      ? 'znak'
      : ilustracja === null || ilustracja === false
        ? 'brak'
        : 'wlasna';
  const tytulDomyslny =
    wariant === 'organ-niedostepny' && organ ? `${organ} chwilowo nie odpowiada` : t.tytul;
  const wpisySzczegolow = szczegoly
    ? (
        [
          ['Kod', szczegoly.kod],
          ['Identyfikator', szczegoly.identyfikator],
          ['Ścieżka', szczegoly.sciezka],
          ['Wersja', szczegoly.wersja],
        ] as const
      ).filter(([, v]) => Boolean(v))
    : [];

  return (
    <Korzen
      ref={ref}
      className={cn('ids-ekran-bledu', `ids-ekran-bledu--${uklad}`, className)}
      data-wariant={wariant}
      data-ilustracja={postac}
      {...props}
    >
      <div className="ids-ekran-bledu__kolumna">
        {postac === 'znak' ? (
          <div className="ids-ekran-bledu__ilustracja">
            {wariant === 'brak-dostepu' ? (
              <LogoInsolitum uklad="sygnet" tytul={null} className="ids-ekran-bledu__spoczynek" />
            ) : (
              <SygnetRozsypany wariant={wariant} />
            )}
          </div>
        ) : postac === 'wlasna' ? (
          <div className="ids-ekran-bledu__ilustracja ids-ekran-bledu__ilustracja--wlasna">
            {ilustracja}
          </div>
        ) : null}
        <p className="ids-ekran-bledu__nadtytul">{bezSierotekWTresci(nadtytul ?? t.nadtytul)}</p>
        <h1 className="ids-ekran-bledu__tytul" tabIndex={-1}>
          {bezSierotekWTresci(tytul ?? tytulDomyslny)}
        </h1>
        <div className="ids-ekran-bledu__tresc">
          {bezSierotekWTresci(tresc ?? <p>{t.tresc}</p>)}
        </div>
        {akcje ? <div className="ids-ekran-bledu__akcje">{akcje}</div> : null}
        {stan !== undefined ? (
          <p className="ids-ekran-bledu__stan" aria-live="polite">
            {stan}
          </p>
        ) : null}
        {wpisySzczegolow.length > 0 ? (
          <details className="ids-ekran-bledu__szczegoly">
            <summary>Szczegóły techniczne</summary>
            <dl>
              {wpisySzczegolow.map(([k, v]) => (
                <div key={k}>
                  <dt>{k}</dt>
                  <dd>
                    <code>{v}</code>
                  </dd>
                </div>
              ))}
            </dl>
          </details>
        ) : null}
      </div>
      {uklad === 'pelny' ? (
        <footer className="ids-podpis-stopka ids-ekran-bledu__podpis">
          {postac === 'znak' ? (
            // Sygnet stoi już nad tytułem — podpisem jest sam napis, tej samej
            // wysokości liter co w logo poziomym podpisu.
            <LogoInsolitum
              uklad="napis"
              wysokosc={SKALA_ZNAKU.napisPodpisu}
              className="ids-podpis"
            />
          ) : (
            <PodpisInsolitum />
          )}
        </footer>
      ) : null}
    </Korzen>
  );
});
